import { TRAJECTORY, REPLAY_WINDOW_END } from "@/content/trajectory";

/** Shared trajectory-sampling math for the two recorded-flight viewers
 * (`TwinReplay`'s 2D canvas replay and `DroneHero3D`'s three.js replay).
 * Both read the same `TRAJECTORY` recording, so the binary-search sample
 * + quaternion nlerp lives here once instead of being duplicated. */

export type V3 = [number, number, number];
/** [w, x, y, z] — matches the trajectory's on-disk quaternion field order. */
export type Q = [number, number, number, number];

/** Recording duration in seconds (last sample's timestamp). */
export const DUR = Math.min(TRAJECTORY[TRAJECTORY.length - 1]![0], REPLAY_WINDOW_END);

export const lerp = (a: number, b: number, f: number) => a + (b - a) * f;

/** Shortest-path normalized lerp between two quaternions (flips `b`'s sign
 * when the dot product is negative so interpolation doesn't take the long
 * way around). Good enough at ~25 Hz sample spacing — no need for full
 * slerp between adjacent recorded frames. */
export function nlerp(a: Q, b: Q, f: number): Q {
  const s = a[0] * b[0] + a[1] * b[1] + a[2] * b[2] + a[3] * b[3] >= 0 ? 1 : -1;
  const q: Q = [lerp(a[0], s * b[0], f), lerp(a[1], s * b[1], f), lerp(a[2], s * b[2], f), lerp(a[3], s * b[3], f)];
  const n = Math.hypot(...q) || 1;
  return q.map((c) => c / n) as Q;
}

/** Sample the recorded trajectory at time `t` (seconds, clamped to the
 * recording's range) via binary search + linear/nlerp interpolation
 * between the two bracketing recorded frames. */
export function sample(t: number): { pos: V3; quat: Q } {
  const T = TRAJECTORY;
  const tc = Math.min(Math.max(t, T[0]![0]), DUR);
  let lo = 0,
    hi = T.length - 1;
  while (hi - lo > 1) {
    const m = (lo + hi) >> 1;
    if (T[m]![0] <= tc) lo = m;
    else hi = m;
  }
  const a = T[lo]!,
    b = T[hi]!,
    f = (tc - a[0]) / (b[0] - a[0] || 1);
  const quat = nlerp([a[4], a[5], a[6], a[7]], [b[4], b[5], b[6], b[7]], f);
  return { pos: [lerp(a[1], b[1], f), lerp(a[2], b[2], f), lerp(a[3], b[3], f)], quat };
}

/** Yaw (rotation about the z/up axis) in radians, extracted from a
 * [w, x, y, z] quaternion. */
export const yawOf = (q: Q) => Math.atan2(2 * (q[0] * q[3] + q[1] * q[2]), 1 - 2 * (q[2] * q[2] + q[3] * q[3]));
